/// <reference types="google-publisher-tag" />

'use client';

import { c as _c } from "react/compiler-runtime";
import { usePostHog } from '@posthog/next';
import { useTranslations } from 'next-intl';
import { useEffect, useEffectEvent, useId, useRef, useState } from 'react';
import { createGptAdTelemetry, type GptTelemetryEvent } from '@/lib/ad-telemetry/gpt';
import { getIsPageVisible, getScreenOrientation } from '@/lib/ad-telemetry/view-context';
import { createViewportTracker } from '@/lib/ad-telemetry/viewport-tracker';
import { env } from '@/lib/runtime-env';
import { cn } from '@/lib/utils';
import { type AdSurface, type AdTelemetryConfig, type AdTelemetryEventName } from '@/types/ad-telemetry';
type AdSlotProps = {
  adSurface: Exclude<AdSurface, 'vast-overlay'>;
  telemetry?: AdTelemetryConfig;
  gamAdUnitPath: string | undefined;
  sizes: googletag.GeneralSize;
  sizeMapping?: googletag.SizeMappingArray | null;
  className?: string;
  contentClassName?: string;
  slotClassName?: string;
  showAdLabel?: boolean;
  refreshIntervalMs?: number;
  onSlotRenderEnded?: (hasRenderedAd: boolean) => void;
};
function AdSlot(t0) {
  const $ = _c(45);
  const {
    adSurface,
    telemetry,
    gamAdUnitPath,
    sizes,
    sizeMapping,
    className,
    contentClassName,
    slotClassName,
    showAdLabel: t1,
    refreshIntervalMs,
    onSlotRenderEnded
  } = t0;
  const showAdLabel = t1 === undefined ? true : t1;
  const posthog = usePostHog();
  const [hasRenderedAd, setHasRenderedAd] = useState(false);
  const [hasSlotRenderEnded, setHasSlotRenderEnded] = useState(false);
  const containerRef = useRef(null);
  const reactId = useId();
  let t2;
  if ($[0] !== reactId) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = /:/g;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = reactId.replace(t3, "");
    $[0] = reactId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const divId = `gam-ad-${t2}`;
  const t = useTranslations("recommend-worlds");
  let t3;
  if ($[3] !== onSlotRenderEnded) {
    t3 = hasRenderedAd_0 => {
      onSlotRenderEnded?.(hasRenderedAd_0);
    };
    $[3] = onSlotRenderEnded;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const notifySlotRenderEnded = useEffectEvent(t3);
  let t4;
  if ($[5] !== posthog) {
    t4 = event => {
      posthog.capture(event.event, event.properties);
    };
    $[5] = posthog;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const captureTelemetry = useEffectEvent(t4);
  let t5;
  if ($[7] !== telemetry) {
    t5 = event_0 => telemetry?.[event_0] ?? false;
    $[7] = telemetry;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const isTelemetryEventEnabled = useEffectEvent(t5);
  let t6;
  if ($[9] !== adSurface || $[10] !== captureTelemetry || $[11] !== divId || $[12] !== gamAdUnitPath || $[13] !== isTelemetryEventEnabled || $[14] !== notifySlotRenderEnded || $[15] !== refreshIntervalMs || $[16] !== sizeMapping || $[17] !== sizes) {
    t6 = () => {
      setHasRenderedAd(false);
      setHasSlotRenderEnded(false);
      if (!gamAdUnitPath) {
        return;
      }
      window.googletag = window.googletag || {
        cmd: []
      };
      let isCancelled = false;
      let refreshTimerId;
      let isRefreshPending = false;
      let retryPendingRefresh;
      const cleanups = [];
      const viewportTracker = refreshIntervalMs || isTelemetryEventEnabled("ad_request_sent") || isTelemetryEventEnabled("ad_impression") ? createViewportTracker(containerRef.current, () => retryPendingRefresh?.()) : undefined;
      const handleVisibilityChange = () => retryPendingRefresh?.();
      if (refreshIntervalMs) {
        document.addEventListener("visibilitychange", handleVisibilityChange);
      }
      const gptTelemetry = createGptAdTelemetry({
        adSurface,
        adUnitPath: gamAdUnitPath,
        capture: captureTelemetry,
        getViewContext: () => {
          const isInViewport = viewportTracker?.isInViewport();
          return {
            ...(isInViewport !== undefined && {
              is_in_viewport: isInViewport
            }),
            is_page_visible: getIsPageVisible(),
            screen_orientation: getScreenOrientation()
          };
        },
        isEventEnabled: isTelemetryEventEnabled
      });
      window.googletag.cmd?.push(() => {
        if (isCancelled) {
          return;
        }
        const slot = googletag.defineSlot(gamAdUnitPath, sizes, divId);
        if (!slot) {
          return;
        }
        if (sizeMapping) {
          slot.defineSizeMapping(sizeMapping);
        }
        if (env("NEXT_PUBLIC_ENVIRONMENT") !== "production") {
          slot.setConfig({
            adsenseAttributes: {
              adsense_test_mode: "on"
            }
          });
        }
        const pubadsService = googletag.pubads();
        slot.addService(pubadsService);
        const addSlotListener = (eventName, handler) => {
          const listener = event_1 => {
            if (!isCancelled && event_1.slot === slot) {
              handler(event_1);
            }
          };
          pubadsService.addEventListener(eventName, listener);
          cleanups.push(() => pubadsService.removeEventListener(eventName, listener));
        };
        const refreshSlot = () => {
          if (isCancelled) {
            return;
          }
          if (document.hidden || viewportTracker?.isInViewport() !== true) {
            isRefreshPending = true;
            return;
          }
          isRefreshPending = false;
          pubadsService.refresh([slot]);
        };
        retryPendingRefresh = () => {
          if (isRefreshPending) {
            refreshSlot();
          }
        };
        const scheduleRefresh = () => {
          if (!refreshIntervalMs) {
            return;
          }
          window.clearTimeout(refreshTimerId);
          isRefreshPending = false;
          refreshTimerId = window.setTimeout(refreshSlot, refreshIntervalMs);
        };
        cleanups.push(() => {
          window.clearTimeout(refreshTimerId);
          isRefreshPending = false;
        });
        addSlotListener("slotRequested", () => gptTelemetry.handleSlotRequested());
        addSlotListener("slotResponseReceived", () => gptTelemetry.handleSlotResponseReceived());
        addSlotListener("slotRenderEnded", event_2 => {
          gptTelemetry.handleSlotRenderEnded(event_2);
          setHasSlotRenderEnded(true);
          if (event_2.isEmpty) {
            setHasRenderedAd(false);
            notifySlotRenderEnded(false);
            return;
          }
          setHasRenderedAd(true);
          notifySlotRenderEnded(true);
          scheduleRefresh();
        });
        addSlotListener("slotOnload", () => gptTelemetry.handleSlotOnload());
        cleanups.push(() => googletag.destroySlots([slot]));
        googletag.setConfig({
          collapseDiv: "BEFORE_FETCH"
        });
        googletag.enableServices();
        googletag.display(slot);
      });
      return () => {
        isCancelled = true;
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        viewportTracker?.dispose();
        notifySlotRenderEnded(false);
        window.googletag?.cmd?.push(() => {
          cleanups.forEach(_temp);
        });
      };
    };
    $[9] = adSurface;
    $[10] = captureTelemetry;
    $[11] = divId;
    $[12] = gamAdUnitPath;
    $[13] = isTelemetryEventEnabled;
    $[14] = notifySlotRenderEnded;
    $[15] = refreshIntervalMs;
    $[16] = sizeMapping;
    $[17] = sizes;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== adSurface || $[20] !== divId || $[21] !== gamAdUnitPath || $[22] !== refreshIntervalMs || $[23] !== sizeMapping || $[24] !== sizes) {
    t7 = [adSurface, gamAdUnitPath, divId, sizes, sizeMapping, refreshIntervalMs];
    $[19] = adSurface;
    $[20] = divId;
    $[21] = gamAdUnitPath;
    $[22] = refreshIntervalMs;
    $[23] = sizeMapping;
    $[24] = sizes;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  useEffect(t6, t7);
  if (!gamAdUnitPath) {
    return null;
  }
  const t8 = hasSlotRenderEnded && !hasRenderedAd && "hidden";
  let t9;
  if ($[26] !== className || $[27] !== t8) {
    t9 = cn("w-full", className, t8);
    $[26] = className;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== contentClassName) {
    t10 = cn("flex w-fit flex-col items-end gap-1", contentClassName);
    $[29] = contentClassName;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== divId || $[32] !== slotClassName) {
    t11 = <div id={divId} className={slotClassName} />;
    $[31] = divId;
    $[32] = slotClassName;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== hasRenderedAd || $[35] !== showAdLabel || $[36] !== t) {
    t12 = showAdLabel && hasRenderedAd && <span className="text-vive-primary-20 text-[9px] leading-4 tracking-[0.15px] md:text-[12px]">{t("advertisement")}</span>;
    $[34] = hasRenderedAd;
    $[35] = showAdLabel;
    $[36] = t;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t10 || $[39] !== t11 || $[40] !== t12) {
    t13 = <div className={t10}>{t11}{t12}</div>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t13 || $[43] !== t9) {
    t14 = <div ref={containerRef} data-component="ad-slot" className={t9}>{t13}</div>;
    $[42] = t13;
    $[43] = t9;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  return t14;
}
function _temp(cleanup) {
  return cleanup();
}
export default AdSlot;